import React, { useCallback, useContext, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import set from 'lodash/set';
import styled from 'styled-components';

import ActionButton from 'src/presentation/components/pages/sidekick/threads/components/ThreadBuilder/ActionButton';
import { ENTER_MANUALLY_OPTION, useCondition } from '../Components/Rule/useCondition';
import { ExternalActionInputOrResult } from 'src/models/Sidekick/SidekickModuleSpec';
import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import FormContext from 'src/presentation/contexts/FormContext';
import { getFriendlyName } from 'src/presentation/components/pages/sidekick/threads/utils/utils';
import ManualInput from './ManualInput';
import MemoSelector, { MemorySelectorResult } from '../Components/Rule/MemorySelector';
import { NextData } from 'src/models/Sidekick/SidekickThreads';
import { OptionalFieldMarker, StyledRow } from 'src/presentation/components/pages/sidekick/threads/components/Layout';
import RefreshStrokeIcon from '@ad-scripts/presentation/components/common/icons/stroke/refresh-stroke';
import { Row } from 'src/presentation/components/forms/Layout';

const implicitDataPullInputs = ['gladlyEntityType', 'gladlyEntityId', 'childCriteria'];

export const isImplicitDataPullInput = (inputName: string) => {
  return implicitDataPullInputs.indexOf(inputName) !== -1;
};

type ExternalActionInputProps = {
  attr: string;
  disabled: boolean;
  input: ExternalActionInputOrResult;
  module: NextData;
};

const ExternalActionInput = ({ attr, disabled, input, module }: ExternalActionInputProps) => {
  const { workingState, onBlur } = useContext(FormContext);

  const [isEnterManually, fields] = useMemo(() => {
    const rawFields = get(workingState, `${attr}.value`) as string | null | undefined;

    if (isUndefined(rawFields)) {
      return [false, []];
    }

    const fieldsStr = rawFields || '';

    const isEnterManually = !hasLiquidBrackets(fieldsStr) && !hasIncompleteLiquidBrackets(fieldsStr);

    if (isEnterManually) {
      return [isEnterManually, [ENTER_MANUALLY_OPTION]];
    }

    return [isEnterManually, fieldsStr.replace('{{', '').replace('}}', '').split('.')];
  }, [attr, workingState]);

  const { currentFields, setCurrentFields, optGroups } = useCondition({
    fields,
    allowEnterManually: true,
    typeSpec: input.typeSpec,
    compatibilityMatch: true,
    includeOnlyNestedProps: false,
  });

  const handleReset = useCallback(() => {
    const newVal = set(cloneDeep(workingState), `${attr}.value`, undefined);
    onBlur && onBlur({ currentState: newVal, needToOverrideState: true });
    setCurrentFields([]);
  }, [attr, onBlur, setCurrentFields, workingState]);

  if (!module.externalAction) {
    return null;
  }

  const skipInput = module.externalAction.dataPull && isImplicitDataPullInput(input.name);
  if (skipInput) {
    return null;
  }

  const handleChangeMemoFields = props => {
    const currentProperties = props.currentState;
    const value = get(currentProperties, `${attr}.value`, []);

    const newProps = { currentState: set(currentProperties, `${attr}.value`, value) };
    onBlur && onBlur(newProps);
  };

  const label = (
    <StyledControlLabel>
      {getFriendlyName(input.name)}
      {input.typeSpec.null && <OptionalFieldMarker>optional</OptionalFieldMarker>}
      {isEnterManually && (
        <StyledActionButton onClick={handleReset} title="Reset">
          <RefreshStrokeIcon width="16" />
        </StyledActionButton>
      )}
    </StyledControlLabel>
  );

  return (
    <Row>
      <FieldContainer
        error={undefined}
        label={label}
        control={
          <StyledRow>
            <MemoSelector
              attr={`${attr}.value`}
              disabled={disabled}
              isEnterManually={isEnterManually}
              optGroups={optGroups}
              fields={currentFields}
              setCurrentFields={setCurrentFields}
              onChange={handleChangeMemoFields}
              resultType={MemorySelectorResult.STRING_WITH_CURLY_BRACKETS}
              renderManualInput={<ManualInput attr={`${attr}`} />}
            />
          </StyledRow>
        }
      />
    </Row>
  );
};

const hasLiquidBrackets = (s: string): boolean => {
  return !!s && String(s).startsWith('{{') && String(s).endsWith('}}');
};

// hasIncompleteLiquidBrackets is used in the input memory selection process
// to indicate a memory has been partially selected, i.e there are more
// "select memory" dropdowns to complete.
export const hasIncompleteLiquidBrackets = (s: string): boolean => {
  return !!s && String(s).startsWith('{{') && !String(s).endsWith('}}');
};

const StyledControlLabel = styled.div`
  display: flex;
  align-items: center;
  color: ${p => p.theme.colors.gray700};
`;

const StyledActionButton = styled(ActionButton)`
  padding: 4px;
  margin-left: 8px;
`;

export default ExternalActionInput;
